import { type MouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router';

/**
 * Keeps the top bar's breadcrumb links SPA transitions, as
 * `useSideNavLogoNavigation` does for the logo.
 *
 * `TopNavBar` renders each crumb with an `href` as a plain `<a>`, so a click
 * hard-navigates and reloads the app. Intercepting on the way up keeps the
 * `href`, so hover, middle-click and open-in-new-tab still behave. Only links to
 * this app are routed, and a modified or new-tab click is left to the browser.
 */
export const useBreadcrumbNavigation = () => {
  const navigate = useNavigate();

  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest<HTMLAnchorElement>('[data-ds-breadcrumb] a[href]');
      if (anchor === null || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      void navigate(`${url.pathname}${url.search}${url.hash}`);
    },
    [navigate],
  );
};
